import { useQuery } from "@tanstack/react-query";
import { aiNexusApiService } from "../services/ai-nexus-api.service";
import type { AiNexusProduct } from "../types/ai-nexus-api.types";
import { GEMS_TIERS_IMAGES, GOLD_TIERS_IMAGES } from "../localConstants/images";

// Extended product interface that includes display properties
export interface Product extends AiNexusProduct {
  type: "ai-credits" | "currency";
  image: string;
  isPopular?: boolean;
  rewardAmount: number;
}

// Helper function to determine product type and reward amount
const getProductDisplayInfo = (
  product: AiNexusProduct
): Pick<Product, "type" | "rewardAmount"> => {
  const reward = product.rewards[0]; // Taking the first reward for simplicity

  return {
    type: reward.type === "ai-credits" ? "ai-credits" : "currency",
    rewardAmount: reward.data.amount,
  };
};

// Helper function to get tier image based on position in sorted array
const getTierImage = (
  index: number,
  type: "ai-credits" | "currency",
  totalCount: number
) => {
  const images = type === "ai-credits" ? GEMS_TIERS_IMAGES : GOLD_TIERS_IMAGES;
  const tierKeys = Object.keys(images) as Array<keyof typeof images>;

  // Distribute products evenly across available tiers
  const tierIndex = Math.min(
    Math.floor((index / totalCount) * tierKeys.length),
    tierKeys.length - 1
  );
  return images[tierKeys[tierIndex]];
};

export const useGetProducts = () => {
  return useQuery({
    queryKey: ["ai-nexus-products"],
    queryFn: async (): Promise<Product[]> => {
      try {
        console.log("🔍 Fetching products from AI Nexus API...");
        const response = await aiNexusApiService.getProducts();

        const activeProducts = response.products.filter(
          (product) => product.active
        );

        // Separate products by type
        const aiCreditsProducts = activeProducts.filter(
          (product) => product.rewards[0]?.type === "ai-credits"
        );
        const currencyProducts = activeProducts.filter(
          (product) => product.rewards[0]?.type !== "ai-credits"
        );

        // Sort each type by reward amount (ascending)
        const sortedAiCredits = aiCreditsProducts.sort(
          (a, b) => a.rewards[0].data.amount - b.rewards[0].data.amount
        );
        const sortedCurrency = currencyProducts.sort(
          (a, b) => a.rewards[0].data.amount - b.rewards[0].data.amount
        );

        // Process AI Credits products
        const processedAiCredits = sortedAiCredits.map((product, index) => {
          const displayInfo = getProductDisplayInfo(product);
          const image = getTierImage(
            index,
            displayInfo.type,
            sortedAiCredits.length
          );

          return {
            ...product,
            ...displayInfo,
            image,
            isPopular: index === Math.floor(sortedAiCredits.length / 2),
          };
        });

        // Process Currency products
        const processedCurrency = sortedCurrency.map((product, index) => {
          const displayInfo = getProductDisplayInfo(product);
          const image = getTierImage(
            index,
            displayInfo.type,
            sortedCurrency.length
          );

          return {
            ...product,
            ...displayInfo,
            image,
            isPopular: index === Math.floor(sortedCurrency.length / 2),
          };
        });

        const processedProducts = [...processedCurrency, ...processedAiCredits];

        console.log("🎯 Final processed products:", processedProducts);
        return processedProducts;
      } catch (error) {
        console.error("❌ Failed to fetch products:", error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });
};
